import { useQuery } from '@tanstack/react-query'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { useTranslate } from 'src/localization/Trns'
import type { AccountCollectionFlowCompletionResponse } from 'src/queries/payments/payments.types'
import {
  useCompleteVendorAccountCollectionFlow,
  useConfirmVendorAccountCollectionFlow,
  useCreateVendorAccountCollectionFlow,
  useRetireVendorPaymentMethod,
  vendorQueries,
} from 'src/queries/vendors/vendors.queries'
import {
  ConfirmVendorAccountCollectionFlowInput,
  Vendor,
  VendorAccountCollectionFlowResponse,
  VendorPaymentMethodDetailsResponse,
  VendorPaymentMethodResponse,
} from 'src/queries/vendors/vendors.types'

import useVendorAccountConfirmation, {
  UseVendorAccountConfirmationResult,
} from './useVendorAccountConfirmation'
import { VENDOR_ACKNOWLEDGEMENT } from './vendorAcknowledgement'

/** Message key for a failed retire, resolved to copy at render time. */
type RemoveErrorKind = 'not-found' | 'concurrent' | 'unexpected'

/**
 * The re-add half's render phase (State B), driven by the reused confirmation
 * hook and whether Modern Treasury has accepted the hosted form. Mirrors the
 * add-vendor flow's own phase machine — the SAME lifecycle code
 * (`useVendorAccountConfirmation`, the account-collection-flow mutation hooks),
 * not a second copy of it.
 */
export type VendorReAddPhase =
  | 'collecting'
  | 'acknowledgement'
  | 'confirming'
  | 'confirmation-pending'
  | 'confirmation-error'

type SubmittedFlow = Pick<VendorAccountCollectionFlowResponse, 'flowId'>

export interface UseVendorPaymentMethodResult {
  /**
   * The one branch predicate for the whole tab: does the vendor have a current
   * ACH method? Computed once here from the vendor DETAIL, falling back to a
   * just-confirmed method only until that detail re-read lands (see
   * `confirmedMethod` below). Never from whether the bank read succeeded — that
   * read returns 200 for a retired method too, and its fields are null on a
   * provider outage. State A and State B are selected in exactly one place.
   */
  hasCurrentAccount: boolean

  // ── State A: read the current account ──────────────────────────────────────
  details: VendorPaymentMethodDetailsResponse | undefined
  isDetailsLoading: boolean
  /** A provider read failure (502). State A stays State A — em-dashes + an error
   * affordance, never the embed. */
  isDetailsError: boolean

  // ── State A: remove behind a confirm ───────────────────────────────────────
  isConfirmingRemoval: boolean
  openRemoveConfirm: () => void
  cancelRemoveConfirm: () => void
  confirmRemoval: () => void
  isRemoving: boolean
  removeError: string | null

  // ── State B: re-add through the Modern Treasury embed ──────────────────────
  phase: VendorReAddPhase
  /** Remount key for the embed — bumped to retry a create-flow that 409'd on
   * the one-open-flow rule. */
  embedKey: number
  /** A just-retired vendor can still carry a stale open collection flow, so
   * creating a new one 409s. Surface a retry rather than a dead end. */
  hasOpenFlowConflict: boolean
  retryReAdd: () => void
  createFlowForEmbed: () => Promise<VendorAccountCollectionFlowResponse>
  completeFlowForEmbed: (
    flowId: string,
  ) => Promise<AccountCollectionFlowCompletionResponse>
  handleCollectionLinked: () => void

  // ── Shared footer contribution (owned by the shell, not this tab) ──────────
  /** True while the ACH acknowledgement is pending — the shell footer shows the
   * ACH checkbox + Confirm and Add. */
  showFooter: boolean
  acknowledged: boolean
  setAcknowledged: (value: boolean) => void
  handleConfirmAndAdd: () => void
  confirmation: UseVendorAccountConfirmationResult
  confirmationErrorMessage: string | null
}

function statusOf(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined
  if ('status' in error && typeof error.status === 'number') return error.status
  return undefined
}

/**
 * Owns the Payment Information tab's data + orchestration for one vendor: the
 * live bank-details read, the retire-behind-a-confirm, and the re-add through
 * the existing Modern Treasury collection → acknowledgement → confirm lifecycle.
 * Reuses the account-collection-flow mutation hooks and
 * {@link useVendorAccountConfirmation} verbatim — the tab adds no second copy of
 * that sequence. Lives at the shell level (VendorEditLoaded) so the single
 * shell-owned footer can render the ACH acknowledgement, and unmounts with the
 * keyed edit view so its state resets per vendor.
 */
export default function useVendorPaymentMethod({
  organizationId,
  vendor,
}: {
  organizationId: string
  vendor: Vendor
}): UseVendorPaymentMethodResult {
  const translate = useTranslate()
  const dispatch = useDispatch()

  // A just-confirmed method, held until the vendor detail re-read catches up.
  //
  // Without it there is a window right after a successful confirm where the
  // predicate is still false: clearing `submittedFlow` drops `phase` to
  // `collecting` in the same synchronous React batch, while the detail refetch
  // is still in flight. State B re-renders, MtAccountCollection remounts, and
  // it creates a flow once on mount — but the vendor now HAS a current ACH
  // method, so the API answers `PaymentMethodAlreadyExists` (409), which this
  // hook then mislabels as a stale open flow. Transient in the happy path,
  // terminal if the refetch fails, since the retry only 409s again.
  //
  // Writing the confirmation into the query cache does NOT close this: React
  // Query notifies observers on a microtask, so the embed still mounts for one
  // render. A plain state fallback flips the predicate in the very same batch.
  //
  // It has to be RETIRED once the detail catches up, not just left to `??`.
  // `??` only defers to the server when the server says something non-null, so
  // a later authoritative `paymentMethod: null` — another actor retires the
  // account and a refetch observes it — would fall through to this stale value
  // and pin the card open for the life of the flyout. The effect below drops it
  // the moment the detail reports the same id, so the fallback covers the
  // post-confirm gap and nothing beyond it.
  const [confirmedMethod, setConfirmedMethod] =
    useState<VendorPaymentMethodResponse | null>(null)

  // Retire the fallback as soon as the detail reports the confirmed method:
  // from then on `vendor.paymentMethod` is the only source, null included.
  useEffect(() => {
    if (confirmedMethod && vendor.paymentMethod?.id === confirmedMethod.id) {
      setConfirmedMethod(null)
    }
  }, [confirmedMethod, vendor.paymentMethod?.id])

  // The one branch predicate. `paymentMethod` is `null` when absent in the
  // mapped domain vendor, so State A ⇔ a non-null method on the DETAIL.
  const currentMethod = vendor.paymentMethod ?? confirmedMethod
  const hasCurrentAccount = currentMethod != null
  const paymentMethodId = currentMethod?.id

  // State A read. Its `enabled` guard needs all three ids, so it self-disables
  // in State B (no method id) — the read never runs when there is no account.
  const detailsQuery = useQuery(
    vendorQueries.paymentMethodDetails(
      organizationId,
      vendor.id,
      paymentMethodId,
    ),
  )

  // ── Remove ──────────────────────────────────────────────────────────────
  const retireMutation = useRetireVendorPaymentMethod(organizationId)
  const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false)
  const [removeErrorKind, setRemoveErrorKind] =
    useState<RemoveErrorKind | null>(null)
  // A synchronous guard: the mutation's `isPending` is a render-time snapshot,
  // so two clicks in the same tick both read `false` and would fire twice. The
  // ref flips immediately, so the second click is a no-op even before a
  // re-render. (The endpoint is idempotent anyway — this just keeps the UI from
  // firing redundant calls.)
  const retiringRef = useRef(false)

  const openRemoveConfirm = useCallback((): void => {
    setRemoveErrorKind(null)
    setIsConfirmingRemoval(true)
  }, [])

  const cancelRemoveConfirm = useCallback((): void => {
    setIsConfirmingRemoval(false)
    setRemoveErrorKind(null)
  }, [])

  const confirmRemoval = useCallback((): void => {
    if (!paymentMethodId || retiringRef.current) return
    retiringRef.current = true
    setRemoveErrorKind(null)
    retireMutation.mutate(
      { vendorId: vendor.id, paymentMethodId },
      {
        // On success the vendor re-reads with no method → State B, so the
        // confirm panel unmounts with the card; closing it here also covers the
        // brief window before the refetch lands.
        onSuccess: () => {
          retiringRef.current = false
          setIsConfirmingRemoval(false)
          // Drop the held method too: the account is gone, so leaving it set
          // would pin the tab to State A after a successful retire.
          setConfirmedMethod(null)
        },
        onError: (error) => {
          retiringRef.current = false
          switch (statusOf(error)) {
            case 404:
              setRemoveErrorKind('not-found')
              break
            case 409:
              setRemoveErrorKind('concurrent')
              break
            default:
              setRemoveErrorKind('unexpected')
          }
        },
      },
    )
  }, [paymentMethodId, retireMutation, vendor.id])

  const removeError =
    removeErrorKind === 'not-found'
      ? translate(
          'This bank account could not be found. It may have already been removed. Close this and reopen the vendor.',
        )
      : removeErrorKind === 'concurrent'
        ? translate(
            'This vendor was updated at the same time. Please try removing the account again.',
          )
        : removeErrorKind === 'unexpected'
          ? translate(
              'Something went wrong removing this account. Please try again.',
            )
          : null

  // ── Re-add (reused Modern Treasury lifecycle) ─────────────────────────────
  const createFlowMutation = useCreateVendorAccountCollectionFlow()
  const completeFlowMutation =
    useCompleteVendorAccountCollectionFlow(organizationId)
  const confirmFlowMutation =
    useConfirmVendorAccountCollectionFlow(organizationId)

  const [submittedFlow, setSubmittedFlow] = useState<SubmittedFlow | null>(null)
  const [acknowledged, setAcknowledged] = useState(false)
  const [hasOpenFlowConflict, setHasOpenFlowConflict] = useState(false)
  const [embedKey, setEmbedKey] = useState(0)
  const activeFlowRef = useRef<VendorAccountCollectionFlowResponse | null>(null)
  const completionStatusRef = useRef<
    'PENDING' | 'AWAITING_ACKNOWLEDGEMENT' | 'CONFIRMED' | null
  >(null)

  const confirmation = useVendorAccountConfirmation({
    confirmFlow: confirmFlowMutation.mutateAsync,
    onConfirmed: (response) => {
      setConfirmedMethod(response.paymentMethod)
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <span>
              <strong>{translate('Payment details updated')}</strong>{' '}
              {translate(
                '${vendorName} is active with new ACH payment details on file.',
                { vendorName: vendor.name },
              )}
            </span>
          ),
        }),
      )
      // The confirm invalidation re-reads the vendor → PAYABLE → State A card.
      // Clear the local re-add state so a later retire starts clean.
      setSubmittedFlow(null)
      setAcknowledged(false)
    },
  })

  const createFlowForEmbed =
    useCallback(async (): Promise<VendorAccountCollectionFlowResponse> => {
      try {
        const flow = await createFlowMutation.mutateAsync(vendor.id)
        activeFlowRef.current = flow
        setHasOpenFlowConflict(false)
        return flow
      } catch (error) {
        // One-open-flow: a just-retired vendor may still hold a stale open flow,
        // so the API rejects a second with 409. Surface a retry instead of the
        // embed's own dead-end error (we render the conflict panel in its place).
        if (statusOf(error) === 409) setHasOpenFlowConflict(true)
        throw error
      }
    }, [createFlowMutation, vendor.id])

  const completeFlowForEmbed = useCallback(
    async (
      flowId: string,
    ): Promise<AccountCollectionFlowCompletionResponse> => {
      const result = await completeFlowMutation.mutateAsync({
        vendorId: vendor.id,
        flowId,
      })
      completionStatusRef.current = result.status
      return {
        status: result.status === 'PENDING' ? 'PENDING' : 'COMPLETED',
      }
    },
    [completeFlowMutation, vendor.id],
  )

  const handleCollectionLinked = useCallback((): void => {
    // A completion that already resolved CONFIRMED needs no acknowledgement; the
    // complete invalidation re-reads the vendor into State A on its own.
    if (completionStatusRef.current === 'CONFIRMED') return
    const flow = activeFlowRef.current
    if (!flow) return
    setAcknowledged(false)
    confirmation.reset()
    setSubmittedFlow({ flowId: flow.flowId })
  }, [confirmation])

  const retryReAdd = useCallback((): void => {
    setHasOpenFlowConflict(false)
    setEmbedKey((value) => value + 1)
  }, [])

  const phase: VendorReAddPhase =
    confirmation.phase === 'error'
      ? 'confirmation-error'
      : confirmation.phase === 'confirming'
        ? 'confirming'
        : confirmation.phase === 'pending'
          ? 'confirmation-pending'
          : submittedFlow
            ? 'acknowledgement'
            : 'collecting'

  const handleConfirmAndAdd = useCallback((): void => {
    const flowId = submittedFlow?.flowId
    if (
      !flowId ||
      !acknowledged ||
      phase !== 'acknowledgement' ||
      confirmation.isInFlight
    ) {
      return
    }
    const input: ConfirmVendorAccountCollectionFlowInput = {
      vendorId: vendor.id,
      flowId,
      acknowledged: true,
      acknowledgementCopyVersion: VENDOR_ACKNOWLEDGEMENT.version,
    }
    confirmation.startConfirmation(input)
  }, [submittedFlow, acknowledged, phase, confirmation, vendor.id])

  const confirmationErrorMessage =
    confirmation.errorKind === 'stale-or-terminal'
      ? translate(
          'This bank account setup can no longer be confirmed. Close this window and start again.',
        )
      : confirmation.errorKind === 'not-found'
        ? translate(
            'This vendor or bank account setup could not be found. Please try again or close this window and start again.',
          )
        : confirmation.errorKind === 'acknowledgement-required'
          ? translate(
              'We could not record your acknowledgement. Please try again.',
            )
          : confirmation.errorKind === 'provider'
            ? translate(
                'We could not confirm this bank account right now. Please try again.',
              )
            : confirmation.errorKind === 'unexpected'
              ? translate(
                  'We received an unexpected confirmation response. Please try again.',
                )
              : null

  const showFooter =
    !hasCurrentAccount && !hasOpenFlowConflict && phase !== 'collecting'

  return {
    hasCurrentAccount,
    details: detailsQuery.data,
    isDetailsLoading: detailsQuery.isLoading,
    isDetailsError: detailsQuery.isError,
    isConfirmingRemoval,
    openRemoveConfirm,
    cancelRemoveConfirm,
    confirmRemoval,
    isRemoving: retireMutation.isPending,
    removeError,
    phase,
    embedKey,
    hasOpenFlowConflict,
    retryReAdd,
    createFlowForEmbed,
    completeFlowForEmbed,
    handleCollectionLinked,
    showFooter,
    acknowledged,
    setAcknowledged,
    handleConfirmAndAdd,
    confirmation,
    confirmationErrorMessage,
  }
}
